import { useQueries } from "@tanstack/react-query";
import { incomeRepository } from "../repositories/incomeRepository";
import { mediumRepository } from "../repositories/mediumRepository";
import { outcomeRepository } from "../repositories/outcomeRepository";
import { terreiroRepository } from "../repositories/terreiroRepository";
import { shouldRetryQuery } from "../utils/queryRetry";
import { financialQueryKeys } from "./financialQueryKeys";
import { useFinancialReadAccess } from "./useFinancialReadAccess";

export { financialQueryKeys };

/**
 * Fetches bank accounts, monthly fees, and current month outcome for a given terreiro or medium in parallel.
 * @param terreiroId The terreiro ID to fetch data for.
 * @param mediumId The medium ID to fetch data for (fallback if terreiroId is missing).
 */
export function useFinancialQueries(
  terreiroId?: string,
  mediumId?: string,
  period?: { month: number; year: number },
) {
  const useTerreiro = !!terreiroId;
  const useMedium = !terreiroId && !!mediumId;
  const {
    canReadBankAccounts,
    canReadMonthlyFees,
    canReadTransactions,
    isResolvingAccess,
  } = useFinancialReadAccess(terreiroId);

  const queries = useQueries({
    queries: [
      {
        queryKey: useTerreiro
          ? financialQueryKeys.bankAccounts(terreiroId)
          : useMedium
          ? financialQueryKeys.mediumBankAccounts(mediumId)
          : ["bank-accounts", "none"],
        queryFn: () => {
          if (useTerreiro) {
            return terreiroRepository.getTerreiroBankAccounts(
              terreiroId as string,
            );
          }
          if (useMedium) {
            return mediumRepository.getBankAccountsByMedium(mediumId as string);
          }
          return Promise.resolve([]);
        },
        enabled: (useTerreiro || useMedium) && canReadBankAccounts,
        staleTime: 1000 * 60 * 5,
        gcTime: 60 * 60 * 1000,
        retry: shouldRetryQuery,
        refetchOnMount: "always" as const,
      },
      {
        queryKey: financialQueryKeys.monthlyFees(terreiroId),
        queryFn: () =>
          terreiroId
            ? terreiroRepository.getMonthlyFees(terreiroId)
            : Promise.resolve([]),
        enabled: !!terreiroId && canReadMonthlyFees,
        staleTime: 1000 * 60 * 5,
        gcTime: 60 * 60 * 1000,
        retry: shouldRetryQuery,
        refetchOnMount: "always" as const,
      },
      {
        queryKey: financialQueryKeys.currentMonthOutcome(
          terreiroId,
          mediumId,
          period?.month,
          period?.year,
        ),
        queryFn: () =>
          useTerreiro || useMedium
            ? outcomeRepository.getCurrentMonth(terreiroId, mediumId, period)
            : Promise.resolve({ month: 0, year: 0, totalAmount: 0 }),
        enabled: (useTerreiro || useMedium) && canReadTransactions,
        staleTime: 1000 * 60 * 5,
        gcTime: 60 * 60 * 1000,
        retry: shouldRetryQuery,
        refetchOnMount: "always" as const,
      },
      {
        queryKey: financialQueryKeys.currentMonthIncome(
          terreiroId,
          mediumId,
          period?.month,
          period?.year,
        ),
        queryFn: () =>
          useTerreiro || useMedium
            ? incomeRepository.getCurrentMonth(terreiroId, mediumId, period)
            : Promise.resolve({ month: 0, year: 0, totalAmount: 0 }),
        enabled: (useTerreiro || useMedium) && canReadTransactions,
        staleTime: 1000 * 60 * 5,
        gcTime: 60 * 60 * 1000,
        retry: shouldRetryQuery,
        refetchOnMount: "always" as const,
      },
    ],
  });

  const [
    bankAccountsQuery,
    monthlyFeesQuery,
    currentMonthOutcomeQuery,
    currentMonthIncomeQuery,
  ] = queries;

  return {
    bankAccounts: bankAccountsQuery.data || [],
    monthlyFees: monthlyFeesQuery.data || [],
    currentMonthOutcome: currentMonthOutcomeQuery.data ?? {
      month: 0,
      year: 0,
      totalAmount: 0,
    },
    currentMonthIncome: currentMonthIncomeQuery.data ?? {
      month: 0,
      year: 0,
      totalAmount: 0,
    },
    isBankAccountsLoading: isResolvingAccess || bankAccountsQuery.isLoading,
    isMonthlyFeesLoading: isResolvingAccess || monthlyFeesQuery.isLoading,
    isCurrentMonthOutcomeLoading:
      isResolvingAccess || currentMonthOutcomeQuery.isLoading,
    isCurrentMonthIncomeLoading:
      isResolvingAccess || currentMonthIncomeQuery.isLoading,
    isAnyLoading: isResolvingAccess || queries.some((q) => q.isLoading),
    bankAccountsError: bankAccountsQuery.error,
    monthlyFeesError: monthlyFeesQuery.error,
    currentMonthOutcomeError: currentMonthOutcomeQuery.error,
    currentMonthIncomeError: currentMonthIncomeQuery.error,
    hasAnyError: queries.some((q) => q.error),
    refetchAll: () => queries.forEach((q) => q.refetch()),
    refetchBankAccounts: bankAccountsQuery.refetch,
    refetchMonthlyFees: monthlyFeesQuery.refetch,
    refetchCurrentMonthOutcome: currentMonthOutcomeQuery.refetch,
    refetchCurrentMonthIncome: currentMonthIncomeQuery.refetch,
    isSuccess: queries.every((q) => q.isSuccess),
    isError: queries.some((q) => q.isError),
  };
}
